import type { ProviderKind } from "@voicely/zod-schemas/enums.zod";
import type {
	ModelCapabilityOffer,
	ProviderCapabilityOffer,
} from "@voicely/zod-schemas/provider.zod";

/**
 * What the provider panel shows, derived from the rows.
 *
 * Extracted from the page for the same reason `engine_section.ts` and
 * `session_lanes.ts` were: the interesting part is a handful of rules about
 * dates and ordering, and a rule that only exists inside a component is a rule
 * nothing can test.
 *
 * The rule that matters most here is the one that looks like an off-by-one:
 *
 * > A retirement date removes a model from every selection list the moment it
 * > is set, not when it arrives.
 *
 * The date says when the VENDOR stops serving the model. There is no reason to
 * keep offering something known to be going away, and every agent created in
 * the gap is one more to migrate. So the panel has three states rather than
 * two, and "retiring" is as hidden from workspaces as "retired" is.
 */

export type RetirementState = "active" | "retiring" | "retired";

export interface PanelModel {
	id: string;
	modelId: string;
	label: string;
	description: string | null;
	languages: string[];
	/**
	 * What ONE socket carries together — the code-switching claim a bilingual
	 * agent's dropdown is gated on. Optional because older list payloads may
	 * predate the column; absent reads as empty.
	 */
	codeSwitching?: string[];
	isDefault: boolean;
	/** Menu order within the vendor. Editable; the rows are read in it. */
	sortOrder: number;
	published: boolean;
	retiresAt: number | null;
	replacedByModelId: string | null;
	/**
	 * Who built the model, when that is not the provider serving it — the
	 * label that lets a vendor-side retirement be found on every provider
	 * carrying the model. Null means the provider is the vendor.
	 */
	vendor?: string | null;
	/** The vendor's own name for the model. Null whenever `vendor` is. */
	vendorModel?: string | null;
}

export interface PanelProvider {
	id: string;
	kind: ProviderKind;
	name: string;
	label: string;
	description: string | null;
	residency: string | null;
	/** Menu order within the leg, and the order the default lane prefers. */
	sortOrder: number;
	published: boolean;
	models: PanelModel[];
}

/**
 * Where a model sits relative to its retirement date.
 *
 * `retiring` and `retired` differ only in what has already happened to calls:
 * both are hidden from every workspace, and neither stops an agent already
 * pinned to the model. Past the date, such an agent fails over instead of
 * failing — which is why "retired" is not "broken" and the panel must not say
 * it is.
 */
export function retirementState(model: PanelModel, now: number): RetirementState {
	if (model.retiresAt === null) return "active";

	return model.retiresAt <= now ? "retired" : "retiring";
}

/** Is this model offered to workspaces right now? */
export function isOffered(model: PanelModel, now: number): boolean {
	return model.published && retirementState(model, now) === "active";
}

/**
 * The sentence under a model's name.
 *
 * Written to answer the question somebody actually has, which is never "what
 * is the timestamp" — it is "is this still working, and do I need to do
 * something". So a retiring model leads with the fact that it is already
 * hidden, because that is the surprising half and the half that is true today.
 */
export function describeRetirement(
	model: PanelModel,
	now: number,
	formatDate: (epochMs: number) => string,
): string | null {
	const state = retirementState(model, now);

	if (state === "active") return null;
	if (model.retiresAt === null) return null;

	const when = formatDate(model.retiresAt);

	if (state === "retiring") {
		return `Hidden from new agents now. The vendor stops serving it on ${when}; agents already on it keep working until then, and fail over after.`;
	}

	return `Retired on ${when}. Agents still on it fail over to a working model rather than stopping.`;
}

/**
 * Whether retiring this model needs somebody to look before they leap.
 *
 * Setting a date whose blast radius you cannot see is scheduling an outage.
 * A template counts for more than an agent even though the number is usually
 * smaller: an agent on a dead model is a migration with a known end, while a
 * TEMPLATE keeps minting new agents onto it until the template is fixed too.
 */
export function retirementWarning(usage: {
	agentCount: number;
	templateCount: number;
}): string | null {
	const parts: string[] = [];

	if (usage.templateCount > 0) {
		parts.push(
			`${usage.templateCount} platform ${usage.templateCount === 1 ? "template" : "templates"} still names it — every new agent cloned from ${usage.templateCount === 1 ? "it" : "them"} starts on this model until ${usage.templateCount === 1 ? "it is" : "they are"} changed`,
		);
	}

	if (usage.agentCount > 0) {
		parts.push(
			`${usage.agentCount} ${usage.agentCount === 1 ? "agent is" : "agents are"} pinned to it and will fail over once the date passes`,
		);
	}

	return parts.length > 0 ? `${parts.join(". ")}.` : null;
}

/**
 * The legs, in the order a turn visits them.
 *
 * Matches the order the catalogue comes back in — `provider_kind_enum` is
 * declared in pipeline order and Postgres sorts enums by declaration — so the
 * panel and the API agree without either sorting the other's output.
 */
export const LEGS: { kind: ProviderKind; label: string; blurb: string }[] = [
	{
		kind: "stt",
		label: "Speech to text",
		blurb:
			"What a caller says, turned into words. Usually the leg with the fewest in-country options, which makes it the binding constraint on which languages can be offered at all.",
	},
	{
		kind: "llm",
		label: "Inference",
		blurb: "What the agent decides to say. Reads the caller's languages and writes the agent's.",
	},
	{
		kind: "tts",
		label: "Synthesis",
		blurb: "The agent's voice. Serves the languages the agent replies in.",
	},
];

/** Providers of one leg, in catalogue order. */
export function byLeg(providers: PanelProvider[], kind: ProviderKind): PanelProvider[] {
	return providers.filter((provider) => provider.kind === kind);
}

/**
 * Every language this leg can serve from India, across offered models.
 *
 * Shown per leg because the intersection across the three is what decides the
 * language menu, and a leg that is the odd one out is the thing somebody needs
 * to see: "synthesis speaks Tamil and nothing can transcribe it" is the shape
 * of every missing language in this product.
 */
export function servedLanguages(
	providers: PanelProvider[],
	kind: ProviderKind,
	now: number,
): string[] {
	const codes = new Set<string>();

	for (const provider of byLeg(providers, kind)) {
		if (!provider.published) continue;

		for (const model of provider.models) {
			if (!isOffered(model, now)) continue;
			for (const code of model.languages) codes.add(code);
		}
	}

	return [...codes].sort();
}

/**
 * Languages ANY leg serves — what a workspace sees in the language menu.
 *
 * The union, beside `offerableAcrossLegs`' intersection: the builder lists a
 * language as soon as one published model serves it and marks the legs still
 * missing, so this is the menu and the intersection is how much of it can
 * carry an agent today.
 */
export function offeredOnAnyLeg(providers: PanelProvider[], now: number): string[] {
	const codes = new Set<string>();

	for (const leg of LEGS)
		for (const code of servedLanguages(providers, leg.kind, now)) codes.add(code);

	return [...codes].sort();
}

/**
 * Languages every leg can serve — the ones an agent can actually be built in.
 *
 * The panel's most useful number, because it is the one that moves when a
 * vendor is added and the one nobody can derive by looking at three lists
 * separately.
 */
export function offerableAcrossLegs(providers: PanelProvider[], now: number): string[] {
	const [first, ...rest] = LEGS.map((leg) => servedLanguages(providers, leg.kind, now));

	if (!first) return [];

	return first.filter((code) => rest.every((set) => set.includes(code)));
}

/**
 * ## The other half: what the forms may offer
 *
 * Everything above reads rows. Everything below reads the runtime's ADAPTER
 * declarations and works out what a form may put in front of somebody, and the
 * distinction is the same one the write path enforces:
 *
 * > The database decides what is OFFERED. The adapter decides what is
 * > POSSIBLE.
 *
 * The write path already refuses a row no adapter can honour, in sentences
 * written to be read. These functions exist so that refusal is the backstop
 * rather than the interface. A vendor key typed from memory, a model id spelled
 * the vendor's way, and eleven ISO codes are four chances to be wrong and four
 * round trips to find out which — when the runtime can simply say what exists.
 */

/** The adapter declaration behind a row, or null when nothing is built for it. */
export function capabilityFor(
	capabilities: ProviderCapabilityOffer[],
	kind: ProviderKind,
	provider: string,
): ProviderCapabilityOffer | null {
	return (
		capabilities.find(
			(capability) => capability.kind === kind && capability.provider === provider,
		) ?? null
	);
}

/**
 * Adapters that exist in the runtime and have no catalogue row at all.
 *
 * This is the "Add a vendor" menu, and it is also the most interesting list on
 * the page: a vendor in it is BUILT and NOT OFFERED, which is a real and
 * deliberate state rather than an oversight. Smallest's Lightning has been in
 * it since its adapter was written — best first byte of any synthesis vendor
 * and it cannot deliver audio as fast as the caller hears it, so the row was
 * never created. Nothing outside the runtime could name that vendor before.
 *
 * `mock` is excluded: `Lane.resolve/1` refuses it in production, so offering it
 * here would be offering a row whose only possible effect is an agent that
 * works on a laptop and fails on its first real call.
 */
export function unofferedAdapters(
	capabilities: ProviderCapabilityOffer[],
	providers: PanelProvider[],
): ProviderCapabilityOffer[] {
	return capabilities.filter(
		(capability) =>
			capability.provider !== "mock" &&
			!providers.some(
				(provider) => provider.kind === capability.kind && provider.name === capability.provider,
			),
	);
}

/**
 * Models this provider's adapter declares that its catalogue rows do not name.
 *
 * The "Add a model" menu. A vendor with none left is not a vendor with a
 * problem — it is one whose catalogue is complete, which is the normal state
 * and should read as such rather than as an empty dropdown.
 */
export function unofferedModels(
	capability: ProviderCapabilityOffer | null,
	provider: PanelProvider,
): ModelCapabilityOffer[] {
	if (!capability) return [];

	return capability.models.filter(
		(model) => !provider.models.some((row) => row.modelId === model.model),
	);
}

/**
 * A row's languages, and its code-switching claim, capped at the adapter's.
 *
 * Both fields are subset-checked server-side and refused with a sentence naming
 * the probe that would fix it. Offering the adapter's own set as the choices is
 * the same rule stated as a menu: a language the adapter does not serve from
 * India is not a typo to be corrected after a round trip, it is a choice that
 * was never available.
 *
 * `codeSwitching` is capped twice — by the adapter's claim AND by whatever the
 * row is currently offering — because it is a subset of `languages` too. A
 * language this row does not serve singly cannot be sold as half of a
 * code-switched pair.
 */
export function offerableCodes(
	capability: ProviderCapabilityOffer | null,
	modelId: string,
): { languages: string[]; codeSwitching: string[] } {
	const model = capability?.models.find((candidate) => candidate.model === modelId);

	return { languages: model?.languages ?? [], codeSwitching: model?.codeSwitching ?? [] };
}

/**
 * Why a model row cannot be published, in one sentence, or null when it can.
 *
 * Checked in the panel as well as on the server because the two answer
 * different questions at different moments. The server answers "may this row be
 * written", on save, and must — it is the boundary. This answers "would
 * publishing this achieve anything", before the click, and catches the case a
 * subset check cannot see: a row that is a perfectly legal narrowing of its
 * adapter and still serves nothing, which is offered to nobody and looks
 * published.
 */
export function publishObjection(
	model: Pick<PanelModel, "languages">,
	capability: ProviderCapabilityOffer | null,
	modelId: string,
): string | null {
	if (!capability) {
		return "No adapter in the runtime is built for this vendor, so nothing can resolve a lane naming it.";
	}

	const declared = capability.models.find((candidate) => candidate.model === modelId);

	if (!declared) {
		return `The adapter does not know the model id "${modelId}". A model the runtime has never heard of cannot open a socket, whatever the row says.`;
	}

	if (model.languages.length === 0) {
		return "This row serves no language from inside India, so no agent can be built on it. Publishing it changes nothing a workspace can see.";
	}

	return null;
}
